import React from 'react';

export function formatInline(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-800">$1</strong>');
}

export function renderMarkdownContent(text: string) {
  if (!text) return <span className="text-gray-400 italic">No data yet</span>;

  const isPlainText = !text.includes('- **') && !text.includes('**Angle');

  if (isPlainText) {
    return renderPlainTextContent(text);
  }

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentAngle: { title: string; items: string[] } | null = null;

  const flushAngle = () => {
    if (currentAngle) {
      elements.push(
        <div key={`angle-${elements.length}`} className="mb-3 last:mb-0">
          <div
            className="text-xs font-bold text-gray-800 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(currentAngle.title) }}
          />
          {currentAngle.items.map((item, i) => (
            <div key={i} className="flex items-start gap-1.5 ml-1 mb-0.5">
              <span className="w-1 h-1 rounded-full bg-[#1875FF]/50 mt-1.5 flex-shrink-0" />
              <span
                className="text-xs text-gray-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatInline(item) }}
              />
            </div>
          ))}
        </div>
      );
      currentAngle = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line === '---') continue;

    const angleMatch = line.match(/^\*\*Angle\s+\d+:.*?\*\*$/);
    if (angleMatch) {
      flushAngle();
      currentAngle = { title: line, items: [] };
      continue;
    }

    const subFieldMatch = line.match(/^\*\*(.+?):\*\*\s*(.*)$/);
    if (subFieldMatch && currentAngle) {
      currentAngle.items.push(`**${subFieldMatch[1]}:** ${subFieldMatch[2]}`);
      continue;
    }

    if (line.startsWith('- ')) {
      const content = line.substring(2).trim();
      if (currentAngle) {
        currentAngle.items.push(content);
      } else {
        elements.push(
          <div key={`bullet-${i}`} className="flex items-start gap-1.5 mb-1.5">
            <span className="w-1 h-1 rounded-full bg-[#1875FF]/50 mt-1.5 flex-shrink-0" />
            <span
              className="text-xs text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatInline(content) }}
            />
          </div>
        );
      }
      continue;
    }

    if (line.startsWith('**') && line.endsWith('**') && !currentAngle) {
      flushAngle();
      currentAngle = { title: line, items: [] };
      continue;
    }

    flushAngle();
    elements.push(
      <p
        key={`para-${i}`}
        className="text-xs text-gray-600 leading-relaxed mb-1.5"
        dangerouslySetInnerHTML={{ __html: formatInline(line) }}
      />
    );
  }

  flushAngle();
  return <>{elements}</>;
}

export function renderPlainTextContent(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentAngle: { title: string; items: string[] } | null = null;

  const flushAngle = () => {
    if (currentAngle) {
      elements.push(
        <div key={`angle-${elements.length}`} className="mb-3 last:mb-0">
          <div className="text-xs font-bold text-gray-800 mb-1">
            {currentAngle.title}
          </div>
          {currentAngle.items.map((item, i) => (
            <div key={i} className="flex items-start gap-1.5 ml-1 mb-0.5">
              <span className="w-1 h-1 rounded-full bg-[#1875FF]/50 mt-1.5 flex-shrink-0" />
              <span className="text-xs text-gray-600 leading-relaxed">{item}</span>
            </div>
          ))}
        </div>
      );
      currentAngle = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const labelMatch = line.match(/^(How to approach|What this means)\s*:\s*(.*)$/i);
    if (labelMatch) {
      flushAngle();
      elements.push(
        <div key={`label-${i}`} className="flex items-start gap-1.5 mb-1.5 mt-1">
          <span className="w-1 h-1 rounded-full bg-[#1875FF]/50 mt-1.5 flex-shrink-0" />
          <span className="text-xs leading-relaxed">
            <strong className="font-semibold text-gray-800">{labelMatch[1]}:</strong>
            <span className="text-gray-600"> {labelMatch[2]}</span>
          </span>
        </div>
      );
      continue;
    }

    const angleHeaderMatch = line.match(/^(.+(?:need|already|sell|have|market|use)\b.+[.!])$/i);
    const isQuotedOpener = line.startsWith('"') && line.endsWith('"');

    if (isQuotedOpener && currentAngle) {
      currentAngle.items.push(line);
      continue;
    }

    if (currentAngle && !isQuotedOpener) {
      const nextLine = i + 1 < lines.length ? lines[i + 1]?.trim() : '';
      const nextIsQuote = nextLine.startsWith('"') && nextLine.endsWith('"');
      if (nextIsQuote || (!nextLine && currentAngle.items.length > 0)) {
        currentAngle.items.push(line);
        continue;
      }
      if (angleHeaderMatch && currentAngle.items.length > 0) {
        flushAngle();
        currentAngle = { title: line, items: [] };
        continue;
      }
      currentAngle.items.push(line);
      continue;
    }

    if (!currentAngle) {
      const nextLine = i + 1 < lines.length ? lines[i + 1]?.trim() : '';
      const lineAfterNext = i + 2 < lines.length ? lines[i + 2]?.trim() : '';
      const nextIsEvidence = nextLine && !nextLine.startsWith('"') && nextLine.length > 0;
      const hasQuoteNearby = (nextLine.startsWith('"') && nextLine.endsWith('"')) ||
        (lineAfterNext.startsWith('"') && lineAfterNext.endsWith('"'));

      if (hasQuoteNearby && nextIsEvidence) {
        flushAngle();
        currentAngle = { title: line, items: [] };
        continue;
      }
    }

    flushAngle();
    elements.push(
      <div key={`bullet-${i}`} className="flex items-start gap-1.5 mb-1.5">
        <span className="w-1 h-1 rounded-full bg-[#1875FF]/50 mt-1.5 flex-shrink-0" />
        <span className="text-xs text-gray-600 leading-relaxed">{line}</span>
      </div>
    );
  }

  flushAngle();
  return <>{elements}</>;
}

export function isInboundInsightsFormat(text: string): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  const inboundHeaders = ['source:', 'action:', 'content:', 'additional details:'];
  const matchCount = inboundHeaders.filter(h => lower.includes(h)).length;
  return matchCount >= 2;
}
